/* eslint-disable @typescript-eslint/no-unused-vars */
import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import {
  BO_VISUAL_DOWNLOAD_OPTION,
  NO_NETWORK_TO_MAP,
} from 'utils/beneficialOwnershipVisualData';

interface IModalCommon {
  handleClose?: any;
  setFileName?: any;
  setDownloadOpts?: any;
  downloadOpts?: string;
  handleCSV?: any;
  triggerCSV: boolean;
  mode?: 'default' | 's3' | 'ieepa' | 'beneficial_ownership';
  hasDiscrepancies?: boolean;
  hasUnmatched?: boolean;
  isOpen?: boolean;
  /** Beneficial Ownership: the report has no network for the visual to map. */
  visualUnavailable?: boolean;
}

const DownloadModal: React.FC<IModalCommon> = ({
  handleClose,
  setFileName,
  setDownloadOpts,
  downloadOpts,
  handleCSV,
  triggerCSV,
  mode = 'default',
  hasDiscrepancies = true,
  hasUnmatched = true,
  isOpen = false,
  visualUnavailable = false,
}) => {
  const [name, setName] = useState('');
  const [status, setStatus] = useState(downloadOpts);

  // The "default" status differs by mode. In IEEPA mode we want the first
  // tile (CAPE Phase I Eligibility Report) pre-selected so clicking
  // Download without touching anything does the sensible default.
  const defaultStatusForMode = () => {
    if (mode === 'ieepa') return 'cape_phase_1';
    if (mode === 's3') return 'original';
    return 'PDF';
  };

  const resetModalState = () => {
    setName('');
    setStatus(downloadOpts || defaultStatusForMode());
    if (setFileName) {
      setFileName('');
    }
    if (setDownloadOpts) {
      setDownloadOpts(defaultStatusForMode());
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setName('');
      setStatus(downloadOpts || defaultStatusForMode());
    }
  }, [downloadOpts, isOpen]);

  const handleTypeName = (e: any) => {
    setFileName(e.target.value);
    setName(e.target.value);
  };

  const handleRowClick = (stat: string, isDisabled = false) => {
    if (isDisabled) return;
    setStatus(stat);
    setDownloadOpts(stat);
  };

  // The header copy and placeholder shift by mode so the user understands
  // what they're downloading without having to read the radio labels.
  const headerTitle =
    mode === 'ieepa'
      ? 'Generate IEEPA Report'
      : mode === 's3'
      ? 'Data Export'
      : 'Download';
  const headerSubtitle =
    mode === 'ieepa'
      ? 'Choose a report type and enter a file name.'
      : 'Enter the desired name for your file.';
  const placeholder =
    mode === 'ieepa'
      ? 'e.g. CAPE Phase I Eligibility'
      : mode === 's3'
      ? 'e.g. Flattened Ace Report'
      : 'e.g. Oligarch';

  return (
    <div className='audit-add-modal-cont'>
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont-change'>
          <div className='audit-add-modal-head-cont-icon'>
            <CloudDownloadOutlinedIcon
              sx={{ color: '#4895EF', width: '25px', height: '24px' }}
            />
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>{headerTitle}</h1>
            <p>{headerSubtitle}</p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={() => {
            resetModalState();
            handleClose?.();
          }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>
      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
      <div
        className='audit-add-modal-cont-body'
        style={{ paddingTop: '12px', paddingBottom: '12px' }}
      >
        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <div className='audit-add-modal-cont-body-frame-form-row-input'>
                <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                  <input
                    className='audit-add-modal-cont-body-frame-form-input'
                    type='text'
                    placeholder={placeholder}
                    value={name}
                    onChange={(e) => {
                      handleTypeName(e);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='audit-add-modal-cont-body-frame'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <div className='audit-add-modal-cont-body-frame-form-row-input'>
                <div className='audit-add-modal-cont-body-frame-form-row-input-label'>
                  <div className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                    {mode === 'ieepa' ? 'Report Type' : 'Download Options'}
                  </div>
                  <div className='bill-container-fit-screen-content-row-selection'>
                    {mode === 'default' && (
                      <>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('PDF')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'PDF' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'PDF' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>PDF</p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('CSV')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'CSV' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'CSV' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>CSV</p>
                          </div>
                        </div>
                      </>
                    )}

                    {mode === 'beneficial_ownership' && (
                      <>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('PDF')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'PDF' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'PDF' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Full report (PDF)</p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() =>
                            handleRowClick(
                              BO_VISUAL_DOWNLOAD_OPTION,
                              visualUnavailable,
                            )
                          }
                          style={{
                            opacity: visualUnavailable ? 0.4 : 1,
                            cursor: visualUnavailable
                              ? 'not-allowed'
                              : 'pointer',
                          }}
                          title={visualUnavailable ? NO_NETWORK_TO_MAP : ''}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === BO_VISUAL_DOWNLOAD_OPTION ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === BO_VISUAL_DOWNLOAD_OPTION ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              {visualUnavailable
                                ? 'Visual summary (no network to map)'
                                : 'Visual summary (one-page PDF)'}
                            </p>
                          </div>
                        </div>
                      </>
                    )}

                    {mode === 's3' && (
                      <>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('original')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'original' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'original' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Original Upload (CSV)</p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('flattened')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'flattened' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'flattened' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Flattend Upload (Horizontal View of Data)</p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() =>
                            handleRowClick('results', !hasDiscrepancies)
                          }
                          style={{
                            opacity: hasDiscrepancies ? 1 : 0.4,
                            cursor: hasDiscrepancies
                              ? 'pointer'
                              : 'not-allowed',
                          }}
                          title={
                            !hasDiscrepancies
                              ? 'No discrepancies were found in this run.'
                              : ''
                          }
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'results' && hasDiscrepancies
                                  ? 'selected'
                                  : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'results' && hasDiscrepancies
                                    ? 'selected'
                                    : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              Analyzed Results (Includes BITE Discrepancies)
                            </p>
                          </div>
                        </div>
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() =>
                            handleRowClick('unmatched', !hasUnmatched)
                          }
                          style={{
                            opacity: hasUnmatched ? 1 : 0.4,
                            cursor: hasUnmatched ? 'pointer' : 'not-allowed',
                          }}
                          title={
                            !hasUnmatched
                              ? 'No unmatched rows were found in this run.'
                              : ''
                          }
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'unmatched' && hasUnmatched
                                  ? 'selected'
                                  : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'unmatched' && hasUnmatched
                                    ? 'selected'
                                    : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>Unmatched Rows (Flattening Logic Outliers)</p>
                          </div>
                        </div>
                      </>
                    )}

                    {mode === 'ieepa' && (
                      <>
                        {/* CAPE Phase I Eligibility Report — applies all 11
                            IEEPA eligibility rules to the cursor query so
                            only refund-eligible lines are included. This
                            mirrors the existing Generate IEEPA Report
                            behavior and is selected by default. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('cape_phase_1')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'cape_phase_1' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'cape_phase_1' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>CAPE Phase I Eligibility Report</p>
                          </div>
                        </div>

                        {/* All IEEPA Rows — skips every eligibility filter
                            except "must carry at least one IEEPA tariff
                            hit". Useful for manual review when the user
                            suspects the eligibility rules are dropping
                            rows they expected to see. Same CSV schema as
                            the eligibility report. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('all_ieepa')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'all_ieepa' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'all_ieepa' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>All IEEPA Rows (No Eligibility Rules Applied)</p>
                          </div>
                        </div>

                        {/* Phase I Exclusions — the complement of the
                            eligibility report. Includes every row that
                            carries an IEEPA tariff hit AND fails at least
                            one eligibility rule (so it would be excluded
                            from CAPE Phase I). Helps operators audit
                            what's being filtered out and why. Same CSV
                            schema as the other two so the output diffs
                            cleanly against the eligibility report. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('phase_1_exclusions')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'phase_1_exclusions'
                                  ? 'selected'
                                  : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'phase_1_exclusions'
                                    ? 'selected'
                                    : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              Phase I Exclusions (IEEPA Rows Not Passing
                              Verfification Checks)
                            </p>
                          </div>
                        </div>

                        {/* CAPE Phase II — Claim Eligibility. Same hard
                            eligibility gate as Phase I, but every surviving
                            line is additionally classified Claimable vs
                            Needs-Review and stamped with trade-remedy /
                            AD-CVD / refund-validation flags plus the ES-003
                            verification-gap disclosures. Needs-Review lines
                            are INCLUDED (flagged) so a reviewer prunes what
                            they reject. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('cape_phase_2')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'cape_phase_2' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'cape_phase_2' ? 'selected' : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              CAPE Phase II — Claim Eligibility (Claimable +
                              Needs Review, Flagged)
                            </p>
                          </div>
                        </div>

                        {/* CAPE Phase II — Exclusions. The Phase II complement:
                            IEEPA-bearing rows that fail at least one hard
                            eligibility gate, rendered in the Phase II column
                            layout with an Exclusion Reasons column. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('phase_2_exclusions')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'phase_2_exclusions' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'phase_2_exclusions'
                                    ? 'selected'
                                    : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              CAPE Phase II — Exclusions (Failed Eligibility
                              Gates)
                            </p>
                          </div>
                        </div>

                        {/* CAPE Phase II — Entry Rollup. One row per entry
                            summary aggregating the claimable-scope lines:
                            line counts by status, total IEEPA duty / refund,
                            AD-CVD totals (kept separate), duplicate detection,
                            and entry-level reconciliation. The claim unit a
                            preparer assembles. */}
                        <div
                          className='bill-container-fit-screen-content-row-checkbox'
                          onClick={() => handleRowClick('cape_phase_2_rollup')}
                        >
                          <div className='bill-container-fit-screen-content-row-input'>
                            <div
                              className={`bill-container-fit-screen-content-row-input-icon ${
                                status === 'cape_phase_2_rollup' ? 'selected' : ''
                              }`}
                            >
                              <div
                                className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                  status === 'cape_phase_2_rollup'
                                    ? 'selected'
                                    : ''
                                }`}
                              />
                            </div>
                          </div>
                          <div className='bill-container-fit-screen-content-row-checkbox-text'>
                            <p>
                              CAPE Phase II — Entry Rollup (One Row per Entry)
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={() => {
              resetModalState();
              handleClose?.();
            }}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() => {
              if (handleCSV) {
                handleCSV({
                  name: name,
                });
              }
            }}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{ color: '#fff' }}
            >
              {triggerCSV && (
                <CircularProgress
                  style={{
                    width: '20px',
                    height: '20px',
                    marginRight: '10px',
                  }}
                  color='primary'
                />
              )}
              Download
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
